import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';
import { trackWhatsAppClick } from '../utils/tracking';

const Services: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [activeTab, setActiveTab] = useState<'worker' | 'corporate' | 'condominium'>('worker');

  const tabs = [
    { id: 'worker', label: t('practiceAreas.worker.title') },
    { id: 'corporate', label: t('practiceAreas.corporate.title') },
    { id: 'condominium', label: t('practiceAreas.condominium.title') }
  ] as const;

  const handleWhatsAppClick = (context: string) => {
    trackWhatsAppClick();
    const phoneNumber = settings?.whatsapp_number || process.env.REACT_APP_WHATSAPP_NUMBER || '5511999999999';
    const message = encodeURIComponent(`Olá, Dra. Luara! Gostaria de falar sobre: ${context}`);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  return (
    <section id="areas-de-atuacao" className="section bg-brand-sand/30">
      <div className="wrapper">
        <div className="max-w-5xl mx-auto">
          {/* Tabs Navigation */}
          <div className="flex flex-col md:flex-row gap-2 mb-12 bg-white p-2 rounded-2xl shadow-sm border border-brand-slate/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex-1 py-4 px-6 rounded-xl font-medium text-center transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-brand-slate text-brand-sand shadow-md'
                    : 'text-brand-ink hover:bg-brand-sand/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-brand-slate/10 min-h-[400px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="max-w-3xl">
                  <h3 className="text-3xl font-heading text-brand-slate mb-6">
                    {t(`practiceAreas.${activeTab}.title`)}
                  </h3>
                  
                  <p className="text-lg text-brand-ink/90 leading-relaxed mb-10">
                    {t(`practiceAreas.${activeTab}.intro`)}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-12">
                    {(t(`practiceAreas.${activeTab}.servicesList`, { returnObjects: true }) as string[]).map((service, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" />
                        <span className="text-brand-ink">{service}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleWhatsAppClick(t(`practiceAreas.${activeTab}.title`))}
                    className="btn-primary inline-flex items-center gap-2 px-8 py-4"
                  >
                    <MessageCircle size={20} />
                    {t(`practiceAreas.${activeTab}.ctaLong`)}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
